import {propertiesUtils} from 'common/report/util/properties';
import {TimeUnitLabels} from 'common/widgets/date-range-picker/models/types';
import * as PropertiesMenu from 'common/query-builder/query-block/properties-menu/types';
import {Segmentation} from 'common/types/reports/query';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {renameEvent} from 'common/report/util/events';
import {ResourceType} from 'common/report/constants';
import {assertUnreachable} from 'common/util/typescript';
import {PATH_SEPARATOR} from 'common/constants';

export function getPropertyPath(
  item: PropertiesMenu.Item | Segmentation,
  dataGroups: DataGroup[] = [],
  {includeResourceLabel = true} = {},
) {
  if (!item) {
    return null;
  }

  // handle behavioral properties which rename as events
  if (`behavior` in item && item.behavior) {
    return {
      name: renameEvent(item.behavior.event.value),
      path: [`Event`],
    };
  }

  let resourceTypeLabel = ``;
  const resourceType = item.resourceType;
  const dataGroupId = `dataGroupId` in item && item.dataGroupId;

  const findMatchingDataGroup = (dataGroupId: string) => {
    if (!dataGroupId) {
      return null;
    }

    return dataGroups.find((dataGroup) => dataGroup.groupID === dataGroupId);
  };

  switch (resourceType) {
    case ResourceType.User:
      // for the new sidebar UI without icons, we display the resource type in the path
      resourceTypeLabel = includeResourceLabel ? findMatchingDataGroup(dataGroupId)?.displayName || `User` : ``;
      break;
    case ResourceType.Cohort:
      resourceTypeLabel = includeResourceLabel ? `In Cohort` : ``;
      break;
    case ResourceType.Event:
    case ResourceType.Other:
    case null:
    case undefined:
      resourceTypeLabel = ``;
      break;
    default:
      assertUnreachable(resourceType);
  }

  const value = (item as PropertiesMenu.Item).value ?? (item as Segmentation).propertyName;

  const propertyValue = [].concat(value, (`propertyObjectKey` in item && item.propertyObjectKey) || []);

  const name = propertyValue[propertyValue.length - 1];
  return {
    name: name ? propertiesUtils.renameProperty(name, name.resourceType) : name,
    path: [resourceTypeLabel, ...propertyValue.slice(0, -1)]
      .filter(Boolean)
      .map((prop) => propertiesUtils.renameProperty(prop, item.resourceType)),
  };
}

export function getPropertyFullLabel(
  item: PropertiesMenu.Item | Segmentation,
  dataGroups: DataGroup[] = [],
  {includeResourceLabel = true} = {},
) {
  if (!item) {
    return null;
  }

  const {name, path} = getPropertyPath(item, dataGroups, {includeResourceLabel});
  return [...path, name].join(PATH_SEPARATOR);
}

export function getPropertyPathPrefix(
  item: PropertiesMenu.Item | Segmentation,
  dataGroups: DataGroup[] = [],
  {includeResourceLabel = true} = {},
) {
  if (!item) {
    return null;
  }

  const {path} = getPropertyPath(item, dataGroups, {includeResourceLabel});
  //  joins paths with non-breaking space and right bullet
  return [...path, ``].join(PATH_SEPARATOR);
}

export function getSelectedPropertyName({
  selected,
  hideSpecialDatePropertyUnit = false,
  expandListProperties = true,
}: {
  selected: PropertiesMenu.Property;
  hideSpecialDatePropertyUnit?: boolean;
  expandListProperties?: boolean;
}) {
  if (!selected) {
    return ``;
  }

  const {name} = getPropertyPath(selected);
  let label = propertiesUtils.renameProperty(name, selected.resourceType, {
    customProperty: selected?.customProperty,
    listItemGroup: expandListProperties ? selected?.listItemGroup : null,
  });

  /** @todo [pablo] find a better workaround for Insights using properties-menu for people mode */
  if (selected.value === `$all_people`) {
    label = selected.label ?? label;
  }

  if (selected.unit && !hideSpecialDatePropertyUnit) {
    label = `${label} (${TimeUnitLabels[selected.unit]})`;
  }

  return label;
}
